import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { Game } from '../room/game';
import { ReplayControls } from './ReplayControls';
import { ReplayEngine } from '../../services/replay/ReplayEngine';
import { ReplayFile, ReplayState } from '../../services/replay/types';
import { Player } from '../room/types';

interface Props {
  engine: ReplayEngine;
  file: ReplayFile;
  profileId: string;
}

export const ReplayRoom: React.FC<Props> = ({ engine, file, profileId }) => {
  const [replayState, setReplayState] = useState<ReplayState>(engine.getState());

  useEffect(() => {
    const unsub = engine.onStateChange((state) => {
      setReplayState(state);
    });
    return unsub;
  }, [engine]);

  const startOffset = Math.max(
    0,
    (file.events.find(
      (e) =>
        e.type === 'game_property_update' &&
        e.payload.property === 'revealQuestion' &&
        e.payload.data !== null
    )?.t ?? 0) - 1000
  );

  useEffect(() => {
    engine.seek(startOffset);
    engine.play();
  }, [engine, startOffset]);

  const { header } = file;

  // Build player objects compatible with what Game expects
  const players: Record<string, Player> = {};
  for (const [id, p] of Object.entries(replayState.players)) {
    players[id] = {
      ...p,
      avatarUrl: p.avatarUrl ?? '',
      avatarPublicId: p.avatarPublicId ?? '',
      fuzzyScore: p.fuzzyScore ?? 0,
      isMobile: false,
      rank: p.rank ?? undefined,
      confirmedReady: false,
      location: undefined,
    } as Player;
  }

  const playerSelf = players[profileId] ?? Object.values(players)[0];

  const playerThem: Player | undefined =
    header.mode === 'match'
      ? Object.values(players).find((p) => p.profileId !== profileId)
      : undefined;

  const fuzzy: Record<string, number> = {};
  for (const [id, p] of Object.entries(replayState.players)) {
    if (p.fuzzyScore != null && p.fuzzyScore > 0) fuzzy[id] = p.fuzzyScore;
  }

  return (
    <Box sx={{ pb: 10 }}>
      <Game
        mode={header.mode}
        gameId={header.gameId}
        gameType="casual"
        profileId={profileId}
        username={playerSelf?.username ?? ''}
        players={players}
        playerSelf={playerSelf}
        playerThem={playerThem}
        myTeam={replayState.myTeam}
        otherTeam={replayState.otherTeam}
        question={replayState.revealQuestion?.revealQuestion ?? ''}
        category={replayState.questionMetadata?.category ?? ''}
        par={replayState.questionMetadata?.par ?? 5}
        questionIndex={replayState.questionIndex}
        answer={replayState.revealAnswer}
        gameClock={replayState.gameClock}
        timerLimitReached={replayState.timerLimitReached}
        gameOver={replayState.gameOver}
        roundOver={replayState.roundOver}
        spectate={true}
        rejoin={false}
        ranked={false}
        paused={false}
        willPause={false}
        fastNext={false}
        freezeGameClock={false}
        displaySize="md"
        categorySet={header.categorySet}
        nextQuestionDelayCount={replayState.nextQuestionDelayCount}
        delayBeforeQuestionRevealCount={0}
        totalNumQuestions={header.questionCount}
        spectatorCount={0}
        spectatorList={[]}
        teams={replayState.myTeam && replayState.otherTeam
          ? { teamA: replayState.myTeam, teamB: replayState.otherTeam }
          : undefined as any}
        variant={header.variant ?? 'normal'}
        survivalMode={header.survivalMode}
        lifes={replayState.survivalChallengeLivesRemaining}
        survivalChallengeStatuses={replayState.survivalChallengeStatuses}
        isAdmin={false}
        liveInputs={replayState.liveInputs}
        fuzzy={fuzzy}
        penalty={replayState.penalty ?? undefined}
        showSnackbarMessage={() => {}}
        handleDisplaySizeChange={() => {}}
        handlePause={() => {}}
        stopSpectating={() => {}}
      />
      <ReplayControls engine={engine} startOffset={startOffset} />
    </Box>
  );
};
